import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import requireUser from '../../lib/requireUser';
import log from '../../lib/log';
import { setUserUnavailable, setUserStatus } from '../../lib/userStatus';
import isValidUser from '../../lib/isValidUser';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';

async function handleAgentAway(event: Telecom.AgentAwayEvent) {
  trackRTCSEventLatency(event.timestamp, RTCSEventType.AGENT_AWAY);

  if (!isValidUser(event.agentId)) {
    return;
  }

  const user = await requireUser(event.agentId);
  log('rtcs:agent:away:' + user?.name, event);

  // forced away due to a missed call. we should set the status to unavailable.
  if (event.awayReason.toLowerCase() === 'force_away') {
    setUserUnavailable(user.id);
  }
  // otherwise, the status set manually so we should just set the status state as away
  else {
    setUserStatus({
      userId: user.id,
      status: event.available
        ? UserSetAvailabilityStatus.Available
        : awayReasonToStatus(event.awayReason),
    });
  }
  user.stats = userStatsFromAgentStats(event.stats);

  // override to current time to workaround Kazoo issue
  user.stats.lastActionAt = new Date().toISOString();
}

function awayReasonToStatus(awayReason: string): UserSetAvailabilityStatus {
  switch (awayReason.toLowerCase()) {
    case 'available':
      return UserSetAvailabilityStatus.Available;
    case 'away from desk':
    case 'away_from_desk':
      return UserSetAvailabilityStatus.AwayFromDesk;
    case 'break':
      return UserSetAvailabilityStatus.Break;
    case 'unavailable':
      return UserSetAvailabilityStatus.Unavailable;
    case 'wrap up':
    case 'extended wrap up':
    case 'extended_wrap_up':
    case 'wrap_up':
      return UserSetAvailabilityStatus.ExtendedWrapUp;
    case 'offline':
      return UserSetAvailabilityStatus.Offline;
    default:
      log(`Unknown away reason: ${awayReason}, defaulting to Unavailable`);
      return UserSetAvailabilityStatus.Unavailable;
  }
}

export default handleAgentAway;
